import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { subscribeLastHumanUpdate } from '../services/activityService';

/**
 * 🕐 Last Update Container
 * 
 * Displays the most recent human-caused activity in 3-line format:
 * Line 1: SITE "KARRATHA DENTAL CARE" STATUS CHANGED TO UNKNOWN
 * Line 2: (Name of person who made the change)
 * Line 3: (Time ago, e.g., "20 minutes ago")
 * 
 * Clicking navigates to the relevant detail page.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - TopCon Dashboard Implementation
 */
const LastUpdateContainer = ({ orgId }) => {
  const navigate = useNavigate();
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    console.log('🕐 LastUpdateContainer: Setting up subscription for org:', orgId);

    const unsubscribe = subscribeLastHumanUpdate(
      orgId,
      (data) => {
        console.log('🕐 LastUpdateContainer: Received data:', data);
        setLastUpdate(data);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('🕐 LastUpdateContainer: Error:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => {
      console.log('🕐 LastUpdateContainer: Cleaning up subscription');
      unsubscribe();
    };
  }, [orgId]);

  // Handle click - navigate to appropriate detail page
  const handleClick = () => {
    if (!lastUpdate) return;

    console.log('🕐 LastUpdateContainer: Clicked, navigating for:', lastUpdate);

    const { entityType, entityId } = lastUpdate;

    try {
      switch (entityType) {
        case 'incident':
          if (entityId) {
            navigate(`/incidents/${entityId}`);
          } else {
            navigate('/incidents');
          }
          break;
        case 'site':
          // Navigate to map or sites page (adjust based on your routing)
          navigate('/dashboard'); // Default to dashboard with map
          break;
        case 'report':
          navigate('/reports');
          break;
        default:
          navigate('/dashboard');
      }
    } catch (error) {
      console.error('🕐 LastUpdateContainer: Navigation error:', error);
      // Fallback to dashboard
      navigate('/dashboard');
    }
  };

  return (
    <section
      style={{
        border: '2px solid #001529',
        borderRadius: '12px',
        padding: '11px',
        backgroundColor: '#ffffff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '11px'
      }}>
        <h3 style={{
          margin: '0',
          fontSize: '14px',
          fontWeight: '700',
          color: '#001529'
        }}>
          Last Update
        </h3>
      </div>

      {/* Content */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        flex: '1',
        minHeight: '80px'
      }}>
        {loading ? (
          <div style={{
            color: '#8c8c8c',
            fontSize: '12px',
            lineHeight: '1.4',
            textAlign: 'center'
          }}>
            Loading...
          </div>
        ) : error ? (
          <div style={{
            color: '#cf1322',
            fontSize: '12px',
            lineHeight: '1.4',
            textAlign: 'center'
          }}>
            Error loading activity
          </div>
        ) : !lastUpdate ? (
          <div style={{
            color: '#8c8c8c',
            fontSize: '12px',
            lineHeight: '1.4',
            textAlign: 'center'
          }}>
            No recent activity
          </div>
        ) : (
          // 3-line format display
          <Tooltip 
            title={`Click to view ${lastUpdate.entityType} details`}
            placement="top"
          >
            <div
              onClick={handleClick}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                padding: '4px',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Line 1: Entity and action */}
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#001529',
                lineHeight: '1.3',
                marginBottom: '2px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>
                {lastUpdate.line1}
              </div>

              {/* Line 2: User name */}
              <div style={{
                fontSize: '11px',
                fontWeight: '500',
                color: '#595959',
                lineHeight: '1.3',
                marginBottom: '2px'
              }}>
                {lastUpdate.line2}
              </div>

              {/* Line 3: Time ago */}
              <div style={{
                fontSize: '10px',
                fontWeight: '400',
                color: '#8c8c8c',
                lineHeight: '1.3'
              }}>
                {lastUpdate.line3}
              </div>
            </div>
          </Tooltip>
        )}
      </div>
    </section>
  );
};

export default LastUpdateContainer;
